import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Send, Paperclip, Camera, CheckCheck, Check, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  dispatchChatList, dispatchChatSend, dispatchChatMarkRead,
  dispatchChatUpload, dispatchChatAttachmentUrl, dispatchChatQuickActions,
} from "@/lib/api";

const POLL_MS = 8000;

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return iso.slice(11, 16); }
};

const isImage = (ct) => (ct || "").startsWith("image/");

/**
 * Shared bidirectional dispatch-chat surface used by both the field portal
 * (staff-token auth) and the admin dispatch dialog (bearer auth).
 *
 * Props:
 *   - leadId          (required)
 *   - token           (staff mode). Absent → admin mode.
 *   - senderRoleHint  ("staff" | "dispatch") used for right-aligning own bubbles
 *                     when the API round-trip hasn't happened yet.
 *   - onNewFromOther  optional callback fired when a new message from the
 *                     other role arrives (used by parent for toast/badge).
 */
export default function DispatchChat({ leadId, token, senderRoleHint = "dispatch", onNewFromOther }) {
  const authOpts = useMemo(() => (token ? { token } : {}), [token]);
  const [messages, setMessages] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState(null); // {file_id, filename, content_type}
  const [autoscroll, setAutoscroll] = useState(true);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const lastMaxTsRef = useRef("");

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Give the DOM a beat to paint the new message before scrolling.
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, []);

  const refresh = useCallback(async (silent = true) => {
    try {
      const { messages: rows } = await dispatchChatList(leadId, authOpts);
      setMessages((prev) => {
        // Detect new-from-other messages so parent can badge/toast.
        const prevIds = new Set(prev.map((m) => m.id));
        const arrivedFromOther = rows.filter(
          (m) => !prevIds.has(m.id) && m.role !== senderRoleHint,
        );
        if (arrivedFromOther.length && onNewFromOther) onNewFromOther(arrivedFromOther);
        return rows;
      });
      // Mark unread messages as read from THIS side.
      const maxTs = rows.reduce((mx, m) => (m.created_at > mx ? m.created_at : mx), "");
      if (maxTs && maxTs !== lastMaxTsRef.current) {
        lastMaxTsRef.current = maxTs;
        try { await dispatchChatMarkRead(leadId, maxTs, authOpts); } catch { /* silent */ }
        if (autoscroll) scrollToBottom();
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Chat load failed.");
    }
  }, [leadId, authOpts, senderRoleHint, onNewFromOther, autoscroll, scrollToBottom]);

  useEffect(() => {
    refresh(false);
    dispatchChatQuickActions(authOpts).then((r) => setQuickActions(r.quick_actions || [])).catch(() => {});
    const t = setInterval(() => refresh(true), POLL_MS);
    return () => clearInterval(t);
  }, [refresh, authOpts]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAutoscroll(nearBottom);
  };

  const send = async ({ textOverride, qa } = {}) => {
    const body = (textOverride ?? text).trim();
    if (!body && !pendingAttachment) return;
    setSending(true);
    try {
      const payload = {
        text: body || (pendingAttachment ? "(attachment)" : ""),
        quick_action: qa || null,
      };
      if (pendingAttachment) {
        payload.attachment_file_id = pendingAttachment.attachment_file_id;
        payload.attachment_filename = pendingAttachment.attachment_filename;
        payload.attachment_content_type = pendingAttachment.attachment_content_type;
      }
      const msg = await dispatchChatSend(leadId, payload, authOpts);
      setMessages((arr) => [...arr, msg]);
      setText("");
      setPendingAttachment(null);
      scrollToBottom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await dispatchChatUpload(leadId, file, authOpts);
      setPendingAttachment(res);
      toast.success(`Attached: ${res.attachment_filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="dispatch-chat">
      {/* Thread */}
      <div ref={scrollRef} onScroll={onScroll}
           className="flex-1 overflow-y-auto p-3 space-y-2"
           style={{ background: "#F8FAFC" }}
           data-testid="dispatch-chat-thread">
        {messages.length === 0 ? (
          <div className="text-center text-xs mt-8" style={{ color: "#94A3B8" }}>
            No messages yet. Say hi or tap a quick action below.
          </div>
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} m={m} isOwn={m.role === senderRoleHint}
                           leadId={leadId} authOpts={authOpts} />
          ))
        )}
      </div>

      {!autoscroll && messages.length > 3 ? (
        <button onClick={() => { setAutoscroll(true); scrollToBottom(); }}
                className="absolute right-4 bottom-24 h-8 w-8 rounded-full shadow-md grid place-items-center"
                style={{ background: "#0B2545", color: "#FFD774" }}
                aria-label="Scroll to newest"
                data-testid="dispatch-chat-scroll-newest">
          <ArrowDown className="h-4 w-4" />
        </button>
      ) : null}

      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div className="px-3 pt-2 flex flex-wrap gap-1.5 border-t" style={{ borderColor: "#E2E8F0", background: "#FFF" }}
             data-testid="dispatch-chat-quick-actions">
          {quickActions.map((qa) => (
            <button key={qa.id}
                    onClick={() => send({ textOverride: qa.template, qa: qa.id })}
                    disabled={sending}
                    className="text-[11px] px-2 py-1 rounded-full border hover:bg-[#F1F5F9]"
                    style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                    data-testid={`chat-quick-${qa.id}`}>
              {qa.label}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="px-3 py-2.5 border-t bg-white flex items-end gap-1.5"
           style={{ borderColor: "#E2E8F0" }}
           data-testid="dispatch-chat-composer">
        <input type="file" ref={fileInputRef} accept="*/*" className="hidden"
               onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="chat-file-input" />
        <input type="file" ref={cameraInputRef} accept="image/*" capture="environment"
               className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])}
               data-testid="chat-camera-input" />
        <button onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="h-9 w-9 grid place-items-center rounded-md border"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                data-testid="chat-camera-button" aria-label="Take specimen photo">
          <Camera className="h-4 w-4" />
        </button>
        <button onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-9 w-9 grid place-items-center rounded-md border"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                data-testid="chat-attach-button" aria-label="Attach file">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          {pendingAttachment ? (
            <div className="text-[10px] mb-1 flex items-center gap-1" style={{ color: "#7A5E12" }}
                 data-testid="chat-pending-attachment">
              📎 {pendingAttachment.attachment_filename}
              <button onClick={() => setPendingAttachment(null)} className="ml-1 underline">remove</button>
            </div>
          ) : null}
          <Input value={text} onChange={(e) => setText(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                 placeholder="Type a message…"
                 data-testid="chat-input"
                 className="h-9" />
        </div>
        <Button onClick={() => send()} disabled={sending || (!text.trim() && !pendingAttachment)}
                className="h-9 px-3" style={{ background: "#0B2545", color: "#FFD774" }}
                data-testid="chat-send-button">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const MessageBubble = ({ m, isOwn, leadId, authOpts }) => {
  const roleLabel = m.role === "staff" ? m.sender_name || "Field" : m.sender_name || "Dispatch";
  const otherRead = m.role === "staff" ? m.read_by_dispatch : m.read_by_staff;
  const attachmentUrl = m.attachment_file_id
    ? dispatchChatAttachmentUrl(leadId, m.attachment_file_id, authOpts)
    : null;

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm text-sm ${isOwn ? "rounded-br-md" : "rounded-bl-md"}`}
           style={{ background: isOwn ? "#0B2545" : "#FFFFFF",
                    color: isOwn ? "#FFFFFF" : "#0B2545",
                    borderWidth: isOwn ? 0 : 1, borderStyle: "solid",
                    borderColor: "#E2E8F0" }}>
        {!isOwn ? (
          <div className="text-[10px] uppercase tracking-wider font-bold mb-0.5"
               style={{ color: "#7A5E12" }}>
            {roleLabel}
          </div>
        ) : null}
        {attachmentUrl && isImage(m.attachment_content_type) ? (
          <a href={attachmentUrl} target="_blank" rel="noreferrer" className="block mb-1">
            <img src={attachmentUrl} alt="attachment"
                 className="rounded-lg max-h-40 max-w-full"
                 data-testid={`chat-attachment-image-${m.id}`} />
          </a>
        ) : attachmentUrl ? (
          <a href={attachmentUrl} target="_blank" rel="noreferrer"
             className="mb-1 underline text-[12px] flex items-center gap-1"
             data-testid={`chat-attachment-link-${m.id}`}
             style={{ color: isOwn ? "#FFD774" : "#2563EB" }}>
            <Paperclip className="h-3 w-3" /> {m.attachment_filename}
          </a>
        ) : null}
        {m.text ? <div className="whitespace-pre-wrap">{m.text}</div> : null}
        <div className="mt-1 text-[9px] flex items-center gap-1"
             style={{ color: isOwn ? "rgba(255,255,255,0.7)" : "#94A3B8" }}>
          <span>{fmtTime(m.created_at)}</span>
          {isOwn ? (
            <span title={otherRead ? "Read" : "Delivered"}>
              {otherRead ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
